<template>
  <div class="relative z-50" :style="tickerPosition" v-click-outside="onClickOutside">
    <a-tooltip>
      <a
        @click="highlightLeg"
        href="#"
        class="relative inline-block text-white no-underline font-bold text-3xl hover:text-gray-200 transition-all text-center p-0.5 notification rounded-full w-10 h-10 -left-4"
        >!
        <span
          class="absolute -right-2.5 -bottom-2.5 w-6 h-6 text-white font-semibold text-xs text-center p-1 notification rounded-xl"
          >{{ notStartedLegsOn.length || 0 }}</span
        >
      </a>
    </a-tooltip>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, PropType } from "vue";
import { buildSearchId, searchIndex } from "@/store/board/useSearchIndex";
import { Leg } from "@/types/leg";
import ClickOutside from "vue-click-outside";
import { BoardEntityType } from "@/types/board";
import { useBoardStore } from "@/store/board/BoardStore";

export default defineComponent({
  name: "Notification",
  directives: { ClickOutside },
  props: {
    notStartedLegsOn: {
      type: Array as PropType<Array<Leg>>,
      default: (): Leg[] => {
        return [];
      }
    },
    tickerPosition: {
      type: String,
      required: true
    }
  },

  setup(props) {
    const boardStore = useBoardStore();

    const ESC_KEY = "Escape";

    const matches = ref<string[]>([]);
    const currentMatch = ref<number>(0);

    const notStartedLegs = props.notStartedLegsOn.map(item => item.id);
    const totalMatches = ref<number>(notStartedLegs.length);

    const increaseCurrentMatch = () => {
      if (totalMatches.value === 0) {
        return;
      }

      currentMatch.value = currentMatch.value < totalMatches.value ? currentMatch.value + 1 : 1;
    };

    const highlightCurrentLeg = async () => {
      const indexToHighlight =
        currentMatch.value !== totalMatches.value
          ? await searchIndex(notStartedLegs[currentMatch.value])
          : await searchIndex(notStartedLegs[0]);
      boardStore.focusedId = indexToHighlight.toString();
      await increaseCurrentMatch();
    };

    const highlightLeg = async (e: Event) => {
      try {
        if (currentMatch.value === 0) {
          boardStore.spottedIds = notStartedLegs.map(legId => buildSearchId(legId, BoardEntityType.LEG));
          await highlightCurrentLeg();
          e.preventDefault();
        } else {
          await highlightCurrentLeg();
          e.preventDefault();
        }
      } catch (error) {
        // do nothing
      }
    };

    const onClickOutside = event => {
      if (currentMatch.value > 0) {
        const element = event.target.closest(".leg");
        const idToCheck = element ? element.getAttribute("id").substring(4) : null;
        if (idToCheck && notStartedLegs.find(legId => legId === idToCheck)) {
          return;
        } else {
          clearSearch();
        }
      }
    };

    const clearSearch = async () => {
      boardStore.spottedIds = [];
      boardStore.focusedId = undefined;
      currentMatch.value = 0;
    };

    const keydownHandler = (e: KeyboardEvent) => {
      const { key: eventKey } = e;
      if (eventKey === ESC_KEY && matches.value) {
        clearSearch();
        return;
      }
    };

    onMounted(() => {
      document.addEventListener("keydown", keydownHandler, true);
    });

    onUnmounted(() => {
      document.removeEventListener("keydown", keydownHandler, true);
    });

    return {
      highlightLeg,
      increaseCurrentMatch,
      clearSearch,
      onClickOutside
    };
  }
});
</script>
<style lang="scss" scoped>
@use "Notification";
</style>
